/**
 * Key layout for the content bucket.
 *
 * ```
 * routes.json                              digit -> category
 * admin/allowlist.json                     who may sign in to the admin site
 * admin/voices.json                        which voices a typed message may use
 * categories/<category>/manifest.json      the category and its messages
 * categories/<category>/<id>/master.mp3    what was generated or uploaded
 * categories/<category>/<id>/phone.mp3     the 8 kHz mono file Twilio plays
 * ```
 */

/** Digit-to-category map. The webhook reads it on every call. */
export const ROUTES_KEY = "routes.json";

/**
 * How long the webhook reuses the manifests and routes it has read (3.2). A
 * content edit shows up a call or two late, which is the trade the project made
 * for one S3 read per category per minute. It lives here because the admin
 * live test waits it out after assigning a digit, and neither side should carry
 * its own copy of the number.
 */
export const ROUTES_CACHE_MS = 60_000;

/** Who may sign in to the admin site. Read by the API and the sign-up trigger. */
export const ALLOWLIST_KEY = "admin/allowlist.json";

/** Which voices staff may pick from. Names the disabled ids; see voices.ts. */
export const VOICES_KEY = "admin/voices.json";

/** Everything the message library owns sits under this. */
export const CATEGORIES_PREFIX = "categories";

/** Served for digits no category claims. Reserved: never assigned to a digit. */
export const NOT_IN_SERVICE = "not-in-service";

/**
 * The operator line (8.1). Digit 0 reads the directory aloud: "For jokes, dial
 * one..." built from the current labels and digit assignments. It is a category
 * like any other, but a generated one — staff never edit it, and it is
 * regenerated from the labels whenever they change. `refreshOperatorGreeting`
 * in the tts package writes it; the webhook serves it through the ordinary path.
 */
export const OPERATOR = "operator";

/** Digit 0 reaches the operator line; the UI hands out 1 through 9 (7.6). */
export const OPERATOR_DIGIT = "0";

/** The one message the operator category holds, replaced on every rebuild. */
export const OPERATOR_MESSAGE_ID = "greeting";

/**
 * Categories staff never see or edit in the admin site: the not-in-service
 * intercept (3.3) and the generated operator line (8.1). Every write route
 * refuses them by name, and `GET /library` leaves them out.
 */
export const RESERVED_CATEGORIES: ReadonlySet<string> = new Set([
  NOT_IN_SERVICE,
  OPERATOR,
]);

/**
 * The words the not-in-service message says. The seeded audio reads this, and
 * the webhook falls back to speaking it when no audio can be served, so the
 * two cannot drift apart.
 */
export const NOT_IN_SERVICE_TEXT =
  "We're sorry. The number you have dialed is not in service. " +
  "Please check the directory card and try your call again.";

/**
 * Longest message any engine will read. A caller is standing at a payphone, and
 * a paste that runs away is a bill as well as a bad message. Roughly 100
 * seconds of speech; see 4.2. It lives here so the admin site's counter and the
 * API's refusal are the same number.
 */
export const MAX_TEXT_CHARACTERS = 1500;

export const MASTER_FILE = "master.mp3";
export const PHONE_FILE = "phone.mp3";

/**
 * Audio a browser may upload as a master (7.3), and the file each one is
 * stored as. MediaRecorder gives webm/opus on Chrome and Firefox and mp4/aac
 * on Safari; the rest are here so a staff member can upload a file they
 * already have. The manifest names the master it means (3.1), so the
 * extensions can differ per message.
 */
const UPLOAD_MASTER_FILES: Record<string, string> = {
  "audio/webm": "master.webm",
  "audio/ogg": "master.ogg",
  "audio/mp4": "master.m4a",
  "audio/mpeg": "master.mp3",
  "audio/wav": "master.wav",
};

/**
 * The type without its codec parameters. MediaRecorder reports the type it
 * recorded with as `audio/webm;codecs=opus`, and the browser sends that whole
 * string as the upload's Content-Type, but only the base type decides what the
 * file is called.
 */
function baseContentType(contentType: string): string {
  return (contentType.split(";")[0] ?? "").trim().toLowerCase();
}

/**
 * Every upload is tagged this on its way in, and the tag is removed once the
 * upload is confirmed (7.4). A bucket lifecycle rule expires anything still
 * carrying it, so a recording a browser PUT and then abandoned — a closed tab,
 * a lost connection before `finishUpload` — does not sit in the bucket forever
 * with no manifest entry pointing at it. The value is `key=value`, the form S3
 * signs into a presigned PUT and matches in a lifecycle filter.
 */
export const UPLOAD_PENDING_TAG_KEY = "phone-booth-pending";
export const UPLOAD_PENDING_TAG_VALUE = "true";
export const UPLOAD_PENDING_TAGGING = `${UPLOAD_PENDING_TAG_KEY}=${UPLOAD_PENDING_TAG_VALUE}`;

/** The master's file name for an uploaded type, or null for a type we refuse. */
export function uploadMasterFile(contentType: string): string | null {
  return UPLOAD_MASTER_FILES[baseContentType(contentType)] ?? null;
}

/**
 * Category and message ids become key segments, and routes.json is data the
 * webhook did not write, so both are checked before a key is built from them.
 */
const NAME = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function isValidName(name: string): boolean {
  return NAME.test(name);
}

/**
 * The id a title becomes: "Nacho cheese!" is `nacho-cheese`, which is both the
 * message id and its key prefix. Null when the title has no usable characters,
 * which each caller words its own refusal for.
 */
export function slugName(title: string): string | null {
  const cleaned = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/g, "");
  return isValidName(cleaned) ? cleaned : null;
}

/**
 * The other direction: `holiday-party` reads as `Holiday party` on the
 * directory card. It is the placeholder title a category gets before anyone
 * renames it (7.6); an existing manifest's own title always wins over it.
 */
export function titleFromName(category: string): string {
  const words = category.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function requireName(kind: string, name: string): string {
  if (!isValidName(name)) throw new Error(`invalid ${kind} name "${name}"`);
  return name;
}

/** Everything a category owns lives under this. The 5.1 IAM policy scopes to it. */
export function categoryPrefix(category: string): string {
  return `${CATEGORIES_PREFIX}/${requireName("category", category)}`;
}

function messagePrefix(category: string, id: string): string {
  return `${categoryPrefix(category)}/${requireName("message", id)}`;
}

export function manifestKey(category: string): string {
  return `${categoryPrefix(category)}/manifest.json`;
}

export function audioKey(category: string, id: string, file: string): string {
  return `${messagePrefix(category, id)}/${file}`;
}

/**
 * Whether a key names a file belonging to that message. A manifest is data the
 * webhook did not write, and its audio key is what gets presigned.
 */
export function isMessageAudioKey(category: string, id: string, key: string): boolean {
  if (!isValidName(category) || !isValidName(id)) return false;
  const prefix = `${messagePrefix(category, id)}/`;
  const file = key.slice(prefix.length);
  return key.startsWith(prefix) && file !== "" && !file.includes("/");
}
